import React, { useState, useRef, useCallback } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";

/**
 * Props for RegionsMultiSelect component
 */
export interface RegionsMultiSelectProps {
  /** List of available region names (voivodeships) to select from */
  options: readonly string[];
  /** Currently selected region names */
  value: string[];
  /** Callback invoked when selected regions change */
  onChange: (value: string[]) => void;
}

export const RegionsMultiSelect: React.FC<RegionsMultiSelectProps> = ({
  options,
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  useClickOutside(dropdownRef, handleClickOutside, isOpen);

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleRegion = (region: string) => {
    if (value.includes(region)) {
      onChange(value.filter((item) => item !== region));
    } else {
      onChange([...value, region]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-left flex items-center justify-between"
      >
        <span className={value.length > 0 ? "text-neutral-900" : "text-neutral-500"}>
          {value.length > 0
            ? `Wybrane regiony: ${value.length}`
            : "Wybierz regiony"}
        </span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg z-[60] max-h-80 overflow-hidden flex flex-col">
          <div className="sticky top-0 bg-white border-b border-neutral-200 p-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Szukaj regionu..."
                className="w-full pl-9 pr-8 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            {value.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="mt-2 text-xs text-primary-600 hover:text-primary-800"
              >
                Wyczyść wszystkie
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1">
            {filteredOptions.length === 0 ? (
              <div className="text-center text-neutral-500 py-4 text-sm">
                Brak pasujących regionów
              </div>
            ) : (
              filteredOptions.map((region) => {
                const isSelected = value.includes(region);
                return (
                  <label
                    key={region}
                    className={`flex items-center gap-2 px-4 py-2 text-sm cursor-pointer hover:bg-neutral-50 ${
                      isSelected ? "bg-primary-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleRegion(region)}
                      className="h-4 w-4 text-primary-600 border-neutral-300 rounded"
                    />
                    <span className="text-sm text-neutral-900">{region}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}

      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2 max-h-32 overflow-y-auto">
          {value.map((region) => (
            <span
              key={region}
              className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 text-neutral-700 text-xs rounded-full"
            >
              {region}
              <button
                type="button"
                onClick={() => toggleRegion(region)}
                className="text-neutral-500 hover:text-neutral-700"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
